import type {
  AdminStory,
  AdminStoryMedia,
  Highlight,
  HighlightCreate,
  HighlightMedia,
  HighlightUpdate,
  StoryCreate,
  StoryMediaPatch,
  StoryPublish,
  StoryUpdate,
} from '@erp/shared';

import { api } from '../../lib/api.js';

// Thin typed client over /stories (admin). Media bytes are streamed from a
// stories-gated raw endpoint, so they are fetched as blobs (bearer-gated) and
// rendered via object URLs — exactly like lib/attachments.fetchAttachmentObjectUrl.

const RAW_HEADERS = { 'Content-Type': 'application/octet-stream' } as const;
const rawConfig = (params: Record<string, string>) => ({
  params,
  headers: RAW_HEADERS,
  transformRequest: [(d: unknown) => d],
});

// ── Stories ──────────────────────────────────────────────────────────────────

export async function listStories(status?: string): Promise<AdminStory[]> {
  const r = await api().get<{ items: AdminStory[] }>('/stories', { params: status ? { status } : {} });
  return r.data.items;
}
export async function getStory(id: string): Promise<AdminStory> {
  return (await api().get<AdminStory>(`/stories/${id}`)).data;
}
export async function createStory(input: StoryCreate): Promise<AdminStory> {
  return (await api().post<AdminStory>('/stories', input)).data;
}
export async function updateStory(id: string, input: StoryUpdate): Promise<AdminStory> {
  return (await api().patch<AdminStory>(`/stories/${id}`, input)).data;
}
export async function deleteStory(id: string): Promise<void> {
  await api().delete(`/stories/${id}`);
}
export async function publishStory(id: string, input: StoryPublish): Promise<AdminStory> {
  return (await api().post<AdminStory>(`/stories/${id}/publish`, input)).data;
}
export async function archiveStory(id: string): Promise<AdminStory> {
  return (await api().post<AdminStory>(`/stories/${id}/archive`, {})).data;
}
export async function reorderStories(ids: string[]): Promise<void> {
  await api().post('/stories/reorder', { ids });
}
export async function setStoryProducts(id: string, itemIds: string[]): Promise<AdminStory> {
  return (await api().put<AdminStory>(`/stories/${id}/products`, { itemIds })).data;
}

// ── Story media ──────────────────────────────────────────────────────────────

export async function addStoryMedia(id: string, file: Blob, filename: string, mimeType: string): Promise<AdminStoryMedia> {
  const body = await file.arrayBuffer();
  return (await api().post<AdminStoryMedia>(`/stories/${id}/media`, body, rawConfig({ filename, mimeType }))).data;
}
export async function setStoryPoster(id: string, mediaId: string, blob: Blob, filename = 'poster.jpg'): Promise<AdminStoryMedia> {
  const body = await blob.arrayBuffer();
  return (await api().post<AdminStoryMedia>(`/stories/${id}/media/${mediaId}/poster`, body, rawConfig({ filename, mimeType: blob.type || 'image/jpeg' }))).data;
}
export async function patchStoryMedia(id: string, mediaId: string, input: StoryMediaPatch): Promise<AdminStoryMedia> {
  return (await api().patch<AdminStoryMedia>(`/stories/${id}/media/${mediaId}`, input)).data;
}
export async function removeStoryMedia(id: string, mediaId: string): Promise<void> {
  await api().delete(`/stories/${id}/media/${mediaId}`);
}
export async function reorderStoryMedia(id: string, ids: string[]): Promise<void> {
  await api().post(`/stories/${id}/media/reorder`, { ids });
}
/** Blob object URL for a media (or its poster). Caller must URL.revokeObjectURL. */
export async function fetchStoryMediaObjectUrl(storyId: string, mediaId: string, variant?: 'poster'): Promise<string> {
  const r = await api().get(`/stories/${storyId}/media/${mediaId}/raw`, {
    responseType: 'blob',
    params: variant ? { variant } : {},
  });
  return URL.createObjectURL(r.data as Blob);
}

// ── Highlights ───────────────────────────────────────────────────────────────

export async function listHighlights(): Promise<Highlight[]> {
  return (await api().get<{ items: Highlight[] }>('/stories/highlights')).data.items;
}
export async function createHighlight(input: HighlightCreate): Promise<Highlight> {
  return (await api().post<Highlight>('/stories/highlights', input)).data;
}
export async function updateHighlight(id: string, input: HighlightUpdate): Promise<Highlight> {
  return (await api().patch<Highlight>(`/stories/highlights/${id}`, input)).data;
}
export async function deleteHighlight(id: string): Promise<void> {
  await api().delete(`/stories/highlights/${id}`);
}
/** Append a frame to a highlight's own media. */
export async function addHighlightMedia(id: string, file: Blob, filename: string, mimeType: string): Promise<HighlightMedia> {
  const body = await file.arrayBuffer();
  return (await api().post<HighlightMedia>(`/stories/highlights/${id}/media`, body, rawConfig({ filename, mimeType }))).data;
}
export async function deleteHighlightMedia(id: string, mediaId: string): Promise<void> {
  await api().delete(`/stories/highlights/${id}/media/${mediaId}`);
}
export async function reorderHighlightMedia(id: string, ids: string[]): Promise<void> {
  await api().post(`/stories/highlights/${id}/media/reorder`, { ids });
}
/** Blob object URL for one frame. Caller must URL.revokeObjectURL. */
export async function fetchHighlightMediaObjectUrl(id: string, mediaId: string): Promise<string> {
  const r = await api().get(`/stories/highlights/${id}/media/${mediaId}/raw`, { responseType: 'blob' });
  return URL.createObjectURL(r.data as Blob);
}
export async function reorderHighlights(ids: string[]): Promise<void> {
  await api().post('/stories/highlights/reorder', { ids });
}
export async function setHighlightCover(id: string, file: Blob, filename = 'cover.jpg'): Promise<Highlight> {
  const body = await file.arrayBuffer();
  return (await api().post<Highlight>(`/stories/highlights/${id}/cover`, body, rawConfig({ filename, mimeType: file.type || 'image/jpeg' }))).data;
}
/** Blob object URL for a highlight cover. Caller must URL.revokeObjectURL. */
export async function fetchHighlightCoverObjectUrl(id: string): Promise<string> {
  const r = await api().get(`/stories/highlights/${id}/cover/raw`, { responseType: 'blob' });
  return URL.createObjectURL(r.data as Blob);
}
